import { getLocalWishlist, getProductGidByHandle, updateRemoteWishlist, CONSTANTS } from "./novicell_wishlist-utils";
import { adaptIconToImage, scheduleIdle } from "./novicell_icon-contrast";

/**
 * Reflect the local wishlist count in the header icon: fill the heart and
 * show a "(n)" bubble next to it. Mirrors the cart icon-bubble pattern, but
 * driven from localStorage since the wishlist lives client-side.
 */
function syncWishlistHeader() {
  const container = document.querySelector('.header__utility--wishlist');
  if (!container) return;

  const count = getLocalWishlist().filter(Boolean).length;
  container.classList.toggle('is-active', count > 0);

  const bubble = container.querySelector('[data-wishlist-count-bubble]');
  if (!bubble) return;

  if (count > 0) {
    bubble.textContent = String(count);
    bubble.hidden = false;
  } else {
    bubble.textContent = '';
    bubble.hidden = true;
  }
}

document.addEventListener('wishlist:updated', syncWishlistHeader);
document.addEventListener('wishlist:removed', syncWishlistHeader);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncWishlistHeader);
} else {
  syncWishlistHeader();
}

/**
 * Shared IntersectionObserver across every <wishlist-button> on the page.
 * On a 40-card collection grid this means one observer instance instead of 40,
 * and the canvas/Image work for any given card is deferred until it's about to
 * scroll into view — the cost scales with what the user actually sees, not
 * with the size of the catalog.
 */
const wishlistViewportObserver = typeof IntersectionObserver !== 'undefined'
  ? new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target); // one-shot per card
        entry.target.runIconAdaptation();
      });
    },
    { rootMargin: '200px' }, // start sampling slightly before the card is on-screen
  )
  : null;

if (!customElements.get('wishlist-button')) {
  class WishlistButton extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.updateWishlistState(false);
      this.initializeEventListeners();
      this.scheduleIconAdaptation();
    }

    disconnectedCallback() {
      // Stop observing if the element is removed before it ever entered the
      // viewport (e.g. infinite-scroll pagination replacing the grid).
      wishlistViewportObserver?.unobserve(this);
    }

    /**
     * Defer the icon-luminance work until the card is near the viewport,
     * then run it during browser idle time so a batch of cards entering
     * together doesn't stall scrolling.
     */
    scheduleIconAdaptation() {
      if (wishlistViewportObserver) {
        wishlistViewportObserver.observe(this);
      } else {
        // No IntersectionObserver — best we can do is defer to idle time.
        scheduleIdle(() => this.runIconAdaptation());
      }
    }

    runIconAdaptation() {
      scheduleIdle(() => this.adaptHeartToImage());
    }

    /**
     * Sample the top-right corner of the closest product image and switch the
     * icon between white and dark depending on the area's brightness, so the
     * heart stays legible on both light and dark imagery.
     */
    adaptHeartToImage() {
      const card = this.closest('.card-wrapper, .card-wrapper-new, .recent-item');
      const sourceImg = card?.querySelector('.card__media img, .media img, img');
      adaptIconToImage(this, sourceImg, {
        corner: 'top-right',
        onLightClass: 'wishlist-button--on-light',
      });
    }

    get button() {
      return this.querySelector('button');
    }

    get productHandle() {
      return this.button?.dataset.productHandle;
    }

    get localWishlist() {
      return getLocalWishlist();
    }

    initializeEventListeners() {
      if (!this.button) return;

      this.button.disabled = false;
      this.button.addEventListener('click', this.handleWishlistToggle.bind(this));
    }

    handleWishlistToggle() {
      this.updateWishlistState();
    }
    
    saveWishlistToLocalStorage(wishlist) {
      const wishlistString = wishlist.join(CONSTANTS.LOCAL_STORAGE_DELIMITER);
      localStorage.setItem(CONSTANTS.LOCAL_STORAGE_WISHLIST_KEY, wishlistString);
    }

    async syncRemoteWishlist() {
      if (CONSTANTS.CUSTOMER_LOGGED_IN) {
        const productGid = await getProductGidByHandle(this.productHandle);
        if (productGid) await updateRemoteWishlist(productGid);
      }
      document.dispatchEvent(
        new CustomEvent('wishlist:updated', {
          detail: { wishlist: this.localWishlist },
        })
      );
    }

    async updateWishlistState(shouldChangeState = true) {
      const wishlist = this.localWishlist;
      const productIndex = wishlist?.indexOf(this.productHandle);
      let isActive = productIndex !== -1;

      if (shouldChangeState) {
        if (isActive) {
          wishlist.splice(productIndex, 1);
        } else {
          wishlist.push(this.productHandle);
        }
        
        isActive = !isActive; 
        this.saveWishlistToLocalStorage(wishlist);
        this.updateButtonAppearance(isActive);

        await this.syncRemoteWishlist();
      } else {
        this.updateButtonAppearance(isActive);
      }
    }

    updateButtonAppearance(isActive) {
      if (isActive) {
        this.classList.add(CONSTANTS.BUTTON_ACTIVE_CLASS);
        this.button.setAttribute('aria-checked', 'true');
      } else {
        this.classList.remove(CONSTANTS.BUTTON_ACTIVE_CLASS);
        this.button.setAttribute('aria-checked', 'false');
      }
    }
  }

  customElements.define('wishlist-button', WishlistButton);
}